<template>
  <div
    :id="`article-${contentItemId}`"
    class="fetched-list-item card card-aside border-0 m-0"
    :class="{ 'bg-light': active }"
    :data-test="`content-list-item-${id}`"
  >
    <label
      class="d-flex flex-column align-self-center m-0 ml-5"
      v-if="showShareButton"
      :id="`disable-shared-tooltip-${id}`"
    >
      <input
        type="checkbox"
        name="custom-switch-checkbox"
        class="custom-switch-input"
        v-model.lazy="contentSet.shared"
        @change="toggleShared()"
      >
      <span class="custom-switch-indicator" />
      <span>Share</span>
    </label>
    <b-tooltip
      :target="`disable-shared-tooltip-${id}`"
      :noninteractive="true"
      v-if="reachedSharedCountMinimum && contentSet.shared"
    >
      Deselecting this item will result in no shared items
    </b-tooltip>
    <span
      v-if="showImage"
      class="card-aside-column ml-5 my-4 d-none d-xl-block"
      :style="`background-image: url('${getImage}')`"
    />
    <div
      class="d-flex flex-column p-0 my-4 ml-5 mr-3"
    >
      <h6
        v-b-tooltip.hover
        :title="getContentURL"
        noninteractive
      >
        {{ getContentTitle }}
      </h6>
      <div
        class="small text-muted"
        v-if="this.state !== null"
      >
        State: {{ state }}
      </div>
      <div class="d-flex align-items-center mt-auto">
        <div v-if="contentSet.updatedDate">
          <small class="d-block text-muted">Edited: {{ editedDate }}</small>
        </div>
        <div
          v-if="commentsCount!==null"
          class="ml-auto text-muted"
        >
          <span
            href="javascript:void(0)"
            class="icon d-none d-md-inline-block ml-3"
            data-test="content-list-item-comments-count"
          >
            <i class="fal fa-comment-alt mr-1" />{{ commentsCount }}
          </span>
        </div>
      </div>
    </div>
    <span
      class="fetched-list-item-overlay"
      @click.self="selectItem"
    />
  </div>
</template>

<script>
import {
  mapActions,
  mapState,
  mapMutations,
  mapGetters,
} from 'vuex';
import * as types from '@/modules/content/store/types';

import suppressibleBox from '@/core/mixins/suppressibleBox';

export default {
  name: 'ShowcaseContentItem',
  mixins: [
    suppressibleBox,
  ],
  props: {
    id: {
      type: Number,
      default: 0,
    },
    contentSet: {
      type: Object,
      default: () => {},
    },
    edited: {
      type: [Date, Number],
      default: 0,
    },
    contentItemId: {
      type: String,
      default: '',
    },
    active: {
      type: Boolean,
      default: false,
    },
    state: {
      type: String,
      default: null,
    },
    commentsCount: {
      type: Number,
      default: null,
    },
    viewType: {
      type: String,
      default: 'Editor',
    },
  },
  data() {
    return {
      clicked: false,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'selectedAdvertiser',
    ]),
    ...mapState('content', [
      'isSelfServiceContent',
      'sharedCount',
      'mode',
      'feedId',
      'contentListType',
      'totalPublished',
    ]),
    ...mapGetters('content', {
      getDisplayCopy: types.GET_CURRENT_SELECTED_CREATIVE_DISPLAY_COPY,
    }),
    editedDate() {
      if (!this.contentSet.updatedDate) return '';
      const date = new Date(this.contentSet.updatedDate);
      return `${date.getDate()}/${(date.getMonth() + 1)}/${date.getFullYear()}`;
    },
    currentRouteName() {
      return this.$route.name;
    },
    showImage() {
      return (this.isSelfServiceContent);
    },
    showShareButton() {
      return (this.currentRouteName === 'Campaign Showcase' && this.mode === 'SHARER');
    },
    reachedSharedCountMinimum() {
      return this.sharedCount === 1;
    },
    getContentTitle() {
      const displayContent = this.getDisplayCopy(this.contentSet.encodedExternalId);
      return displayContent && displayContent.title ? displayContent.title : this.contentSet.title;
    },
    getContentURL() {
      const displayContent = this.getDisplayCopy(this.contentSet.encodedExternalId);
      return displayContent && displayContent.url ? displayContent.url : this.contentSet.url;
    },
    getImage() {
      const displayContent = this.getDisplayCopy(this.contentSet.encodedExternalId);
      const image = displayContent && displayContent.imageFileName ? displayContent.imageFileName : this.contentSet.imageUrl;
      return image != null ? image : '/images/image-not-found.png';
    },
  },
  methods: {
    ...mapActions('content', [
      types.UPDATE_CONTENT_ITEM,
      types.TOGGLE_CONTENT_SHARED,
    ]),
    ...mapMutations('content', [
      types.SET_CONTENT_TOTAL_PUBLISHED_ITEMS,
    ]),
    selectItem() {
      if (this.contentItemId !== null) this.$emit('selectItem', this.contentItemId);
    },
    toggleShared() {
      if (this.clicked) return;
      this.clicked = true;
      const payload = {
        canShare: this.contentSet.shared,
        contentItemId: this.contentSet.encodedExternalId,
      };
      this.TOGGLE_CONTENT_SHARED(payload)
        .then(() => {
          this.clicked = false;
        });
    },
  },
  created() {
    if (this.contentSet.id === 0) this.$emit('selectItem', this.contentItemId);
  },
};
</script>
<style lang="scss">
.fetched-list-item{
  min-height: 80px;
}
.fetched-list-item-overlay {
    cursor: pointer;
    position: absolute;
    left: 85px;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
}
.fetched-list-item a, .fetched-list-item select{
    z-index: 2;
    position: relative;
}
.card-aside-column{
  min-height: 104px;
}
.control-state-button{
  z-index: 2;
  position: relative;
  cursor: pointer;
}
</style>
